import { useEffect, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { Icon } from "./Icon";
import { useI18n } from "../lib/i18n";
import { resolveResourceUrls } from "../lib/data";
import { formatBytes } from "../lib/files";
import { printResource } from "../lib/resource-print";
import type { ActivityResource, ResourceKind } from "../types";

/**
 * The material attached to an activity, rendered where the parent is standing.
 *
 * The old version listed everything as a link, which meant a two-sentence
 * example and a fifteen-minute video looked identical and both cost a tap. A
 * picture of the finished tally chart is the answer to "what am I aiming for?"
 * — it should be visible, not linked. So the medium decides: text and pictures
 * render in place, audio and video get a player, and only files that genuinely
 * live elsewhere stay a link.
 */

export const resourceIcon: Record<ResourceKind, string> = {
  worksheet: "edit",
  pdf: "book",
  image: "image",
  video: "play",
  audio: "volume",
  text: "message",
  link: "arrow",
  reading: "book",
  document: "folder",
};

/**
 * Signed URLs for a set of resources.
 *
 * One batched call per set rather than one per row: an R2 URL is minted by the
 * Worker, and a guide with four pictures should not cost four round trips
 * before the first one appears.
 */
export function useResourceUrls(supabase: SupabaseClient, resources: ActivityResource[]) {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [settled, setSettled] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  // Keyed on what the URLs are derived from, rather than on the array, which
  // the parent rebuilds every render. The target has to be part of the key as
  // well as the id: replacing the file behind a resource keeps its id, and
  // keying on the id alone would leave the admin's preview showing the URL of
  // the file they just replaced.
  const signature = resources
    .map((resource) => `${resource.id}:${resource.storage_provider}:${resource.storage_path ?? ""}:${resource.url ?? ""}`)
    .join(",");
  const requestKey = `${signature}:${attempt}`;

  useEffect(() => {
    if (resources.length === 0) {
      setUrls({});
      setSettled(requestKey);
      return;
    }
    let cancelled = false;
    void resolveResourceUrls(supabase, resources).then((resolved) => {
      if (!cancelled) { setUrls(resolved); setSettled(requestKey); }
    }).catch(() => {
      if (!cancelled) { setUrls({}); setSettled(requestKey); }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supabase, requestKey]);

  return { urls: settled === requestKey ? urls : {}, loading: settled !== requestKey, retry: () => setAttempt(n => n + 1) };
}

function ResourceMeta({ resource }: { resource: ActivityResource }) {
  const { t } = useI18n();
  const size = formatBytes(resource.size_bytes);
  return (
    <small>
      {t(`resources.kind.${resource.kind}`)}
      {size && ` · ${size}`}
    </small>
  );
}

/**
 * One resource.
 *
 * `href` is null while the URL is being signed, and stays null if the file has
 * gone — the row is then shown disabled rather than as a link that does
 * nothing, which is the difference between "loading" and "broken" from the
 * parent's side of the screen.
 */
function ResourceItem({ resource, href, loading }: { resource: ActivityResource; href: string | null; loading: boolean }) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);

  if (resource.body) {
    // Long text folds away so a guide does not become an essay; short text is
    // shorter than the button that would hide it.
    const long = resource.body.length > 320;
    return (
      <div className="resource-note">
        <div className="resource-note-head">
          <span className="icon-tile gold">
            <Icon name={resource.is_example ? "star" : "message"} size={16} />
          </span>
          <strong dir="auto">{resource.title}</strong>
        </div>
        <p dir="auto" className={long && !expanded ? "clamped" : ""}>{resource.body}</p>
        {long && (
          <button className="link-button" onClick={() => setExpanded(!expanded)}>
            {expanded ? t("resources.showLess") : t("resources.showMore")}
          </button>
        )}
        <button type="button" className="link-button resource-print" onClick={() => printResource(resource)}>
          <Icon name="download" size={14} /> {t("resources.print")}
        </button>
        {resource.note && <p className="resource-hint">{resource.note}</p>}
      </div>
    );
  }

  if (resource.kind === "image" && resource.storage_path) {
    return (
      <figure className="resource-figure">
        {href ? (
          <a href={href} target="_blank" rel="noreferrer noopener">
            <img src={href} alt={resource.title} loading="lazy" />
          </a>
        ) : (
          <div className="resource-placeholder" role="status">{t(loading ? "resources.loading" : "resources.unavailable")}</div>
        )}
        <figcaption>
          <strong>{resource.title}</strong>
          {resource.note && <span>{resource.note}</span>}
        </figcaption>
      </figure>
    );
  }

  if ((resource.kind === "video" || resource.kind === "audio") && resource.storage_path) {
    return (
      <figure className="resource-figure">
        {href ? (
          resource.kind === "video" ? (
            // No autoplay and no preload: a parent on mobile data should not pay
            // for a video they have not asked to watch.
            <video src={href} controls preload="none" playsInline />
          ) : (
            <audio src={href} controls preload="none" />
          )
        ) : (
          <div className="resource-placeholder" role="status">{t(loading ? "resources.loading" : "resources.unavailable")}</div>
        )}
        <figcaption>
          <strong>{resource.title}</strong>
          {resource.note && <span>{resource.note}</span>}
        </figcaption>
      </figure>
    );
  }

  return (
    <a
      className="resource-row"
      href={href ?? undefined}
      target="_blank"
      rel="noreferrer noopener"
      aria-disabled={href ? undefined : true}
    >
      <span className={`icon-tile ${resource.is_example ? "gold" : "blue"}`}>
        <Icon name={resourceIcon[resource.kind] ?? "folder"} size={16} />
      </span>
      <div>
        <strong>{resource.title}</strong>
        <ResourceMeta resource={resource} />
        {!href && <small role="status">{t(loading ? "resources.loading" : "resources.unavailable")}</small>}
        {resource.note && <p>{resource.note}</p>}
      </div>
      <Icon name={resource.storage_path ? "download" : "arrow"} size={16} />
    </a>
  );
}

/**
 * Everything attached to one activity, materials and examples kept apart.
 *
 * They are separated because they answer different questions. "What do I
 * print?" and "what should this look like when it works?" are both useful and
 * neither is served by being mixed into one list.
 */
export function ResourcePanel({
  supabase,
  resources,
}: {
  supabase: SupabaseClient;
  resources: ActivityResource[];
}) {
  const { t } = useI18n();
  const { urls, loading, retry } = useResourceUrls(supabase, resources);
  const unavailable = !loading && resources.some(r => (r.url || r.storage_path) && !urls[r.id]);

  if (resources.length === 0) return null;

  const ordered = [...resources].sort((a, b) => a.sort_order - b.sort_order);
  const materials = ordered.filter((resource) => !resource.is_example);
  const examples = ordered.filter((resource) => resource.is_example);

  return (
    <>
      {unavailable && <p role="status" className="resource-hint">{t("resources.unavailable")} <button type="button" className="link-button" onClick={retry}>{t("resources.retry")}</button></p>}
      {materials.length > 0 && (
        <div className="resource-block">
          <small className="eyebrow">{t("resources.title")}</small>
          {materials.map((resource) => (
            <ResourceItem key={resource.id} resource={resource} href={urls[resource.id] ?? null} loading={loading} />
          ))}
        </div>
      )}
      {examples.length > 0 && (
        <div className="resource-block">
          <small className="eyebrow">{t("resources.examples")}</small>
          <p className="resource-hint">{t("resources.examplesCopy")}</p>
          {examples.map((resource) => (
            <ResourceItem key={resource.id} resource={resource} href={urls[resource.id] ?? null} loading={loading} />
          ))}
        </div>
      )}
    </>
  );
}
